import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {
  Button,
  Col,
  Icon,
  Modal,
  ModalFooter,
  Row,
  TextField,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import FieldsBuilder from '../FieldsBuilder';
import LocalizedTextField from '../../LocalizedTextField';

function AddNewSectionForm({
  open,
  onClose,
  fieldArrayName,
  fieldName,
  initialValues,
  actionType,
}) {
  const { fields } = useFieldArray(`templateFields.${fieldArrayName}`);
  const intl = useIntl();

  /** Handle calling submit button from outside the final form */
  let submit;

  const onHandleSubmit = (values) => {
    if (actionType === 'create') {
      fields.push({
        sectionId: uuidv4(),
        sectionLabel: values.sectionLabel,
        sectionFields: values.sectionFields,
      });
    } else {
      const sectionIndex = fields.value.findIndex(
        (field) => field.sectionId === initialValues.sectionId,
      );
      fields.update(sectionIndex, {
        sectionId: uuidv4(),
        sectionLabel: values.sectionLabel,
        sectionFields: values.sectionFields,
      });
    }

    onClose();
  };

  const renderFooter = (pristine, submitting, values, form) => {
    return (
      <ModalFooter>
        <Button
          id="inventory-template-plugin-add-field-modal-footer-add-btn"
          buttonStyle="primary"
          type="submit"
          onClick={(event) => {
            submit(event);
          }}
          disabled={pristine || submitting || !values.sectionLabel}
        >
          <Icon icon={actionType === 'create' ? 'save' : 'edit'}>
            {actionType === 'create' ? (
              <FormattedMessage
                id="stripes-core.button.saveAndClose"
                defaultMessage="Save & Close"
              />
            ) : (
              <FormattedMessage
                id="ui-plugin-inventory-templates.buttons.updateAndClose"
                defaultMessage="Update & Close"
              />
            )}
          </Icon>
        </Button>
        <Button
          id="inventory-template-plugin-add-field-modal-footer-close-btn"
          buttonStyle="slim"
          onClick={() => {
            onClose();
            form.reset();
          }}
        >
          <Icon icon="times-circle-solid" size="large">
            <FormattedMessage id="stripes-core.button.close" />
          </Icon>
        </Button>
      </ModalFooter>
    );
  };

  const SectionForm = (
    <Form
      onSubmit={onHandleSubmit}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
      }}
    >
      {({ handleSubmit, pristine, submitting, values, form }) => {
        submit = handleSubmit;
        return (
          <Modal
            id="inventory-template-plugin-add-field-modal"
            footer={renderFooter(pristine, submitting, values, form)}
            open={open}
            onClose={onClose}
            dismissible
            label={
              <Icon icon={actionType === 'create' ? 'plus-sign' : 'edit'}>
                {actionType === 'create' ? (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.addNewSectionModal.label.create"
                    defaultMessage="Add new section"
                  />
                ) : (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.addNewSectionModal.label.edit"
                    values={{
                      sectionName: initialValues.sectionLabel.find(
                        (locale) => locale.locale === intl.locale,
                      )?.value,
                    }}
                    defaultMessage="Edit {sectionName} section"
                  />
                )}
              </Icon>
            }
            size="large"
          >
            <form id="new-section-form" onSubmit={handleSubmit}>
              <Row>
                <Col xs={6}>
                  <FieldArray
                    id={`localizedArrayField-${fieldName}`}
                    name="sectionLabel"
                    component={LocalizedTextField}
                    fieldComponent={TextField}
                    fieldLabel={
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.templateSection.fields.label"
                        defaultMessage="Section Label"
                      />
                    }
                    isRequired
                  />
                </Col>
              </Row>
              <FieldArray
                id={`template-fields-builder-${fieldName}`}
                name="sectionFields"
                component={FieldsBuilder}
                fieldArrayName={fieldArrayName}
                parentFields={fields}
              />
            </form>
          </Modal>
        );
      }}
    </Form>
  );

  /** Put the form inside new react portal to fix the nested form issue */
  const container = document.getElementById('ModuleContainer');
  return ReactDOM.createPortal(SectionForm, container);
}

AddNewSectionForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  fieldArrayName: PropTypes.string,
  fieldName: PropTypes.string,
  initialValues: PropTypes.object,
  actionType: PropTypes.string,
};

export default AddNewSectionForm;
